import React from 'react';
import { CodeInput } from '@plerionhq/design-system';

const MIN_JSON_VIEWER_HEIGHT = 120;
const MAX_JSON_VIEWER_HEIGHT = 500;

type PolicyJsonViewerProperties = {
  document: unknown;
};

export function PolicyJsonViewer({ document }: PolicyJsonViewerProperties) {
  const json = React.useMemo(() => {
    if (typeof document !== 'string') {
      return JSON.stringify(document, null, 2);
    }
    // Stored policies are JSON strings that may be compact (single line) — some
    // collectors JSON.stringify without indentation, and several AWS APIs return
    // minified documents. Re-indent so the viewer pretty-prints them; fall back to
    // the raw string if it isn't valid JSON.
    try {
      return JSON.stringify(JSON.parse(document), null, 2);
    } catch {
      return document;
    }
  }, [document]);
  const [height, setHeight] = React.useState(MIN_JSON_VIEWER_HEIGHT);

  return (
    <div style={{ height }}>
      <CodeInput
        value={json}
        readOnly={true}
        className="w-full border-none"
        style={{ height: '100%' }}
        enableSearch={false}
        showCopyButton={false}
        showExpandIcon={false}
        options={{
          scrollBeyondLastLine: false,
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          stickyScroll: { enabled: false },
          renderLineHighlight: 'none',
          matchBrackets: 'never',
          occurrencesHighlight: 'off',
          selectionHighlight: false,
          scrollbar: {
            alwaysConsumeMouseWheel: false,
            verticalScrollbarSize: 8,
          },
        }}
        onMount={(editor) => {
          const updateHeight = () => {
            const contentHeight = Math.max(
              MIN_JSON_VIEWER_HEIGHT,
              Math.min(editor.getContentHeight(), MAX_JSON_VIEWER_HEIGHT),
            );
            setHeight(contentHeight);
          };
          updateHeight();
          // Monaco disposes its own listeners on editor.dispose() at unmount.
          editor.onDidContentSizeChange(updateHeight);
        }}
      />
    </div>
  );
}
